import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Race, StoreRacePayload, UpdateRacePayload } from '@/types/events'
import {
  listRaces as apiListRaces,
  createRace as apiCreateRace,
  getRace as apiGetRace,
  updateRace as apiUpdateRace,
  deleteRace as apiDeleteRace,
} from '@/api/events'

const races = ref<Race[]>([])
const race = ref<Race | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchRaces(eventSlug: string, editionSlug: string): Promise<void> {
  loading.value = true
  error.value = null
  try {
    races.value = await apiListRaces(eventSlug, editionSlug)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function fetchRace(eventSlug: string, editionSlug: string, raceSlug: string): Promise<void> {
  loading.value = true
  error.value = null
  try {
    race.value = await apiGetRace(eventSlug, editionSlug, raceSlug)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createRace(
  eventSlug: string,
  editionSlug: string,
  payload: StoreRacePayload,
): Promise<Race> {
  const created = await apiCreateRace(eventSlug, editionSlug, payload)
  races.value = [...races.value, created]
  return created
}

async function updateRace(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
  payload: UpdateRacePayload,
): Promise<Race> {
  const updated = await apiUpdateRace(eventSlug, editionSlug, raceSlug, payload)
  race.value = updated
  races.value = races.value.map((r) => (r.slug === raceSlug ? updated : r))
  return updated
}

async function deleteRace(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
): Promise<void> {
  await apiDeleteRace(eventSlug, editionSlug, raceSlug)
  races.value = races.value.filter((r) => r.slug !== raceSlug)
}

export function useRaces(): {
  races: Ref<Race[]>
  race: Ref<Race | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchRaces: (eventSlug: string, editionSlug: string) => Promise<void>
  fetchRace: (eventSlug: string, editionSlug: string, raceSlug: string) => Promise<void>
  createRace: (eventSlug: string, editionSlug: string, payload: StoreRacePayload) => Promise<Race>
  updateRace: (
    eventSlug: string,
    editionSlug: string,
    raceSlug: string,
    payload: UpdateRacePayload,
  ) => Promise<Race>
  deleteRace: (eventSlug: string, editionSlug: string, raceSlug: string) => Promise<void>
} {
  return {
    races,
    race,
    loading,
    error,
    fetchRaces,
    fetchRace,
    createRace,
    updateRace,
    deleteRace,
  }
}
